import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceZonesModal = ({ isOpen, onClose, zones }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Filtrer les zones selon les nouveaux champs
  const filteredZones = zones.filter(zone => 
    zone.postal_code.includes(searchTerm) ||
    zone.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    zone.municipality_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    zone.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Trier par priorité (les priorités les plus basses d'abord)
  const sortedZones = [...filteredZones].sort((a, b) => (a.priority || 999) - (b.priority || 999));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      
      <div 
        ref={modalRef}
        className="relative h-[108vh] flex items-center justify-center p-4"
      >
        <div className="relative bg-card rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom-4">
          {/* Header avec gradient */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Zones d'intervention
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {zones.length} secteur{zones.length > 1 ? 's' : ''} desservi{zones.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {zones.filter(z => z.is_active).length} actif{zones.filter(z => z.is_active).length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-black/10 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Barre de recherche */}
            <div className="mt-4 relative">
              <Icon 
                name="Search" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Rechercher un code postal, une ville ou un secteur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Content avec scroll */}
          <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(76vh - 200px)' }}>
            {sortedZones.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="MapPin" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun secteur trouvé</p>
                {searchTerm && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSearchTerm('')}
                    className="mt-4"
                  >
                    Effacer la recherche
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sortedZones.map((zone) => (
                  <div 
                    key={zone.id}
                    className={`group p-4 border rounded-lg transition-all cursor-pointer ${
                      zone.is_active 
                        ? 'border-border hover:border-primary/30 hover:bg-primary/5' 
                        : 'border-border/50 bg-muted/20 opacity-75'
                    }`}
                   
                  >
                    <div className="flex items-start gap-3">
                      {/* Indicateur de priorité/statut */}
                       
                      
                      <div className="flex-1 min-w-0">
                        {/* Code postal et nom du secteur */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-medium text-foreground">
                            {zone.postal_code}
                          </span>
                          {zone.name && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-sm font-medium text-foreground truncate">
                                {zone.name}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Municipalité/Ville */}
                        {zone.municipality_name && (
                          <p className="text-sm text-foreground mt-1">
                            {zone.municipality_name}
                          </p>
                        )}

                      

                        {/* Rayon d'intervention */}
                        {zone.radius_km && (
                          <p className="text-xs text-muted-foreground mt-1">
                            ⭕ Rayon : {zone.radius_km} km
                          </p>
                        )}

                        {/* Notes */}
                        {zone.notes && (
                          <p className="text-xs text-muted-foreground mt-2 italic line-clamp-2">
                            "{zone.notes}"
                          </p>
                        )}

                        {/* Badge inactif */}
                        {!zone.is_active && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                            Temporairement indisponible
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer avec stats et infos */}
          <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {sortedZones.length} résultat{sortedZones.length > 1 ? 's' : ''} affiché{sortedZones.length > 1 ? 's' : ''}
              {searchTerm && sortedZones.length !== zones.length && ` sur ${zones.length} total`}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {zones.filter(z => z.is_active).length} actifs • {zones.filter(z => !z.is_active).length} inactifs
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceZonesModal;